/**
 * Schedule page, allows the user to set their preferences for the charging session.
 * @module
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Form, FormGroup, Alert, Navbar, Nav, NavItem } from 'reactstrap';
import { FormProvider, SubmitHandler, useForm, useWatch } from 'react-hook-form';

import '../widgets/EnergySelector';
import '../widgets/ModeSelector';
import ModeSelector from "../widgets/ModeSelector";
import EnergySelector from "../widgets/EnergySelector";
import { ChargingMode } from "../../data/models/ChargingMode";
import { GlobalButton } from "../styled/Button";
import { useHistory } from "react-router-dom";
import '../../styles/schedule.scss';
import * as Types from "../../App";

import axios from 'axios';

import { setCookie, getCookie } from "../../data/Cookies";
import TimeInput from '../widgets/TimeInput';
import { Button, Container, Grid } from '@mui/material';
import PlanGraph from '../widgets/PlanGraph';

import useProfileData from "../../hooks/useProfileData.js"
import toast, { Toaster } from 'react-hot-toast';
import useSuggestion from '../../hooks/useSuggestion';
import { GiBackwardTime } from 'react-icons/gi'

/**
 * TODO add docs
 */
type ScheduleInput = {
    arrival: Date,
    departure: Date,
    finished: Date,
    isAborted: boolean,
    mode: ChargingMode | null,
    price: number,
    CO2: number,
    desiredEnergy: number,
    actualEnergy: number,
    planning: number[],
};

/**
 * TODO add docs
 */
interface ScheduleProps {
    settings: Types.SessionType,
    setSettings: (values: Types.SessionType) => void,
}

/**
 * Constructs the page.
 * @param settings         Seetings of the charging session: mode, departure time and etc. (defined in App.tsx)
 * @param setSettings      Setter for the state
 */
export default function Schedule({ settings, setSettings }: ScheduleProps) {

    const history = useHistory();
    /**
     * Default values of the selectors.
     */
    const initialValues = {
        arrival: new Date(),
        departure: new Date(),
        finished: new Date(),
        isAborted: false,
        mode: null,
        price: 0,
        CO2: 0,
        desiredEnergy: 0,
        actualEnergy: 0,
        planning: [],
    }
    const form = useForm({
        defaultValues: { ...initialValues },
    });

    // Example implementation of the useSuggestion hook
    const { suggestion, getSuggestion } = useSuggestion();
    const [alert, setAlert] = useState<boolean>(false);

    // makes sure that the charging notification for session only displayed 
    // when schedule is previous page
    localStorage.setItem('firstChargeNotification', 'true');

    useEffect(() => {
        const values = {
            hour: form.getValues("departure").getHours(),
            minutes: form.getValues("departure").getMinutes(),
            mode: form.getValues("mode"),
            price: form.getValues("price"),
            energy: form.getValues("desiredEnergy"),
            CO2: form.getValues("CO2"),
            planning: form.getValues("planning"),
        }
        setSettings({ ...values })
    }, [form]);

    // Actions when submitting the form
    const onSubmit: SubmitHandler<ScheduleInput> = useCallback((data) => {
        if (form.getValues("desiredEnergy") !== 0 && form.getValues("mode") != null) {
            const values = {
                hour: form.getValues("departure").getHours(),
                minutes: form.getValues("departure").getMinutes(),
                mode: form.getValues("mode"),
                price: form.getValues("price"),
                energy: form.getValues("desiredEnergy"),
                CO2: form.getValues("CO2"),
                planning: form.getValues("planning"),
            }
            setSettings(values)
            //FIXME change url
            axios.post('https://slimpark.roaming.utwente.nl:3001/planning', {
                "data": data,
                "id": getCookie("id"),
                "hours": form.getValues("departure").getHours(),
                "minutes": form.getValues("departure").getMinutes(),
            })
                .then(function (response) {
                    console.log(response);
                })
                .catch(function (error) {
                    console.log(error);
                });
            toast.dismiss("CloudyToast");
            history.push("/session");

        } else {
            console.log(form)
            setAlert(true);
            setTimeout(() => { setAlert(false) }, 2000);
        }
    }, [history, form])

    // Check solar energy predictions and give recommendation 
    const { profileData, getProfileData } = useProfileData();

    useEffect(() => {
        getProfileData();
        getSuggestion();
    }, [])

    useEffect(() => {
        console.log("Suggestion:" , suggestion)
    }, [suggestion])

    // Toast for informing the user the car is charging
    const notify = () => toast('Cloudy! Limited renewable energy available', {
        id: 'CloudyToast',
        duration: 10000,
        position: 'top-center',

        icon: '❗',

        style: {
            maxHeight: '30px',
            minWidth: '400px',
            border: '1px solid red',
            padding: '16px',
            color: 'black',
            fontSize: '15px',
        },
    });

    useEffect(() => {
        // Check the sun prediction for the next 8 hours
        let cloudy = true;
        for (let i = 0; i < 32; i++) {
            var prediction = profileData.desired_profile[i];
            if (prediction !== 0 && !Number.isNaN(prediction)) {
                cloudy = false;
            }
        }

        if (cloudy) {
            notify();
        }

    }, [])


    const handleSuggestion = () => {
        form.setValue("desiredEnergy", suggestion.energy)
    }

    // Resulting page
    return (
        <Container className='schedule'>
            <Toaster
                containerStyle={{
                    top: 80,
                }} />
            <Alert className={"scheduleAlert"} isOpen={alert} color="danger">Energy and mode are required</Alert>
            <FormProvider {...form}>
                <Form onSubmit={form.handleSubmit(onSubmit)}>

                    <FormGroup style={{ marginTop: "3rem" }}>
                        <Grid container
                            direction="row"
                            alignItems="center"
                        >
                            <Grid item xs={7}>
                                <h3>Departure Time</h3>
                            </Grid>
                            <Grid item xs>
                                <TimeInput />
                            </Grid>
                        </Grid>
                    </FormGroup>

                    <FormGroup style={{ marginTop: "2rem" }}>
                        <h3>Energy</h3>
                        <EnergySelector />
                    </FormGroup>

                    <FormGroup style={{ marginTop: "2rem" }}>
                        <h3>Charging Mode</h3>
                        <ModeSelector />
                    </FormGroup>

                    <Grid marginTop={'2rem'} alignItems={'center'} justifyContent={'center'} display={'flex'}>
                        <Button
                            variant='outlined'
                            color='success'
                            onClick={handleSuggestion}
                            sx={{
                                borderRadius: '15px',
                                textTransform: 'none',
                                fontWeight: '600',
                                fontFamily: 'Open Sans',
                                border: '2px solid #77c768',
                                padding: '5px',
                            }}>
                            <Grid container direction={'column'}>
                                <h5>Use Suggested Settings</h5>
                            </Grid>
                        </Button>
                    </Grid>

                    <FormGroup style={{ marginTop: "2rem" }}>
                        <h3 style={{ marginBottom: "2rem" }}>Predicted Energy Production</h3>
                        {/* <Graph /> */}
                        <PlanGraph />
                    </FormGroup>

                    <FormGroup style={{ textAlign: "center", marginTop: "3rem" }}>
                        <GlobalButton text={"Schedule"} type={"submit"} />
                    </FormGroup>

                </Form>
            </FormProvider>

        </Container>

    );
};

/*
<Container className="schedule">
            <Navbar className={"navbarSchedule"} light sticky={"top"}>
                <Nav style={{width: "100%"}} navbar>
                    <NavItem>
                        <Container>
                            <Row>
                                <Col style={{paddingLeft: "2rem"}}><h4 style={{fontSize: "2vh"}}>Price: €{ price ? price.toFixed(2) : 0}</h4></Col>
                                <Col style={{textAlign: "center"}}><h4 style={{fontSize: "2vh"}}>CO2: {CO2 ? CO2.toFixed(1) : 0}g </h4></Col>
                            </Row>
                        </Container>
                    </NavItem>
                </Nav>
            </Navbar>
            <Alert className={"scheduleAlert"} isOpen={alert} color="danger">Energy and mode are required</Alert>
            <Row style={{marginRight: "1rem", marginLeft: "1rem"}}>
                <Col>
                    <h1 className={"responsiveTitle"}>
                        Hello, { document.cookie }!
                    </h1>
                    <FormProvider {...form}>
                        <Form onSubmit={form.handleSubmit(onSubmit)}>                            
                            <FormGroup style={{marginTop: "1rem"}}>
                                <StepIcon step={"1"} text={"Select Departure Time"}/>
                                <TimeSelector/>
                            </FormGroup>
                            <FormGroup style={{marginTop: "1rem"}}>
                                <StepIcon step={"2"} text={"Select Desired Energy"}/>
                                <EnergySelector/>
                            </FormGroup>
                            <FormGroup style={{marginTop: "1rem"}}>
                                <StepIcon step={"3"} text={"Select Charging Mode"}/>
                                <ModeSelector/>
                            </FormGroup>
                            <FormGroup style={{marginTop: "1rem"}}>
                                <StepIcon step={"4"} text={"Checkout the schedule!"}/>
                                <Graph/>
                            </FormGroup>
                            <FormGroup style={{textAlign: "center"}}>
                                <GlobalButton text={"Go"} type={"submit"}/>
                            </FormGroup>
                        </Form>
                    </FormProvider>
                </Col>
            </Row>
        </Container>
*/




